import L from 'leaflet'
import type { TravelLog } from './types'
import { state } from './state'
import { t } from './i18n'
import { renderMedia } from './media'
import { findCountryFeature } from './countries'

/** The single Leaflet map instance for the app. */
export const map = L.map('map', {
  center: [20, 0],
  zoom: 2,
  minZoom: 2,
  maxZoom: 16,
  worldCopyJump: true,
  zoomControl: false,
})

L.control.zoom({ position: 'bottomleft' }).addTo(map)
L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
  attribution: '&copy; OpenStreetMap contributors',
}).addTo(map)

/** Resizes the Leaflet view when the map container size changes. */
export function attachResizeObserver(): void {
  const mapEl = document.querySelector('#map')
  if (!mapEl) return
  const ro = new ResizeObserver(() => {
    window.requestAnimationFrame(() => map.invalidateSize())
  })
  ro.observe(mapEl)
  window.addEventListener('load', () => map.invalidateSize())
}

// ---- Draft marker (shown while creating a new item) -------------------------

export function renderDraftMarker(latlng: L.LatLng): void {
  if (state.draftMarker) state.draftMarker.remove()
  state.draftMarker = L.circleMarker(latlng, {
    radius: 8,
    color: '#ffffff',
    weight: 2,
    fillColor: '#1f8a5b',
    fillOpacity: 1,
    interactive: false,
  }).addTo(map)
}

export function clearDraftMarker(): void {
  if (state.draftMarker) {
    state.draftMarker.remove()
    state.draftMarker = null
  }
}

// ---- Country layer (colored polygons) ---------------------------------------

/** Rebuild the country choropleth. The click handler bubbles up to main. */
export function renderCountryLayer(
  onCountryClick: (latlng: L.LatLng) => void,
): void {
  if (!state.countries.length) return
  if (state.countryLayer) state.countryLayer.remove()

  const visited = new Set(state.items.map((i) => i.country).filter(Boolean))
  const isDark = state.theme === 'dark'

  state.countryLayer = L.geoJSON(
    {
      type: 'FeatureCollection',
      features: state.countries,
    } as GeoJSON.FeatureCollection,
    {
      bubblingMouseEvents: false,
      style: (feature) => {
        const name = (feature?.properties as { name?: string } | undefined)
          ?.name
        const v = !!name && visited.has(name)
        const sel = name === state.selectedCountry
        return {
          color: sel
            ? isDark
              ? '#8fb8ff'
              : '#173f7a'
            : v
              ? isDark
                ? '#36c57e'
                : '#1f8a5b'
              : isDark
                ? '#31423a'
                : '#ffffff',
          weight: sel ? 2 : v ? 1.2 : 0.5,
          opacity: sel ? 0.95 : v ? 0.76 : 0.34,
          fillColor: sel
            ? isDark
              ? '#24466f'
              : '#dbeafe'
            : v
              ? isDark
                ? '#36c57e'
                : '#2fbf75'
              : isDark
                ? '#16211d'
                : '#f7f9f8',
          fillOpacity: sel ? 0.34 : v ? 0.22 : isDark ? 0.08 : 0,
        }
      },
      onEachFeature: (feature, layer) => {
        const name = (feature.properties as { name?: string } | undefined)
          ?.name
        layer.on('click', (event: L.LeafletMouseEvent) => {
          if (event.originalEvent) L.DomEvent.stop(event.originalEvent)
          onCountryClick(event.latlng)
        })
        if (name) layer.bindTooltip(name, { sticky: true })
      },
    },
  ).addTo(map)
}

// ---- Item pins --------------------------------------------------------------

export interface MarkerControls {
  onEdit: (item: TravelLog) => void
  onDelete: (item: TravelLog) => void
}

export function renderMarkers(
  onMarkerClick: (item: TravelLog) => void,
  controls?: MarkerControls,
): void {
  if (state.markerLayer) state.markerLayer.remove()
  state.markerLayer = L.layerGroup()

  state.items.forEach((item) => {
    L.circleMarker([item.lat, item.lng], {
      radius:
      //  item.kind === 'travel-log' ? 6 :
        7,
      color: '#ffffff',
      weight: 1.5,
      fillColor:
        // item.kind === 'travel-log'
          // ? 
          '#1f8a5b',
          // : item.kind === 'photo'
          //   ? '#2f6fed'
          //   : '#b35cff',
      fillOpacity: 0.95,
    })
      .bindPopup(() => renderPopup(item, controls), { maxWidth: 320 })
      .on('click', () => onMarkerClick(item))
      .addTo(state.markerLayer!)
  })

  state.markerLayer.addTo(map)
}

function renderPopup(item: TravelLog, controls?: MarkerControls): HTMLElement {
  const wrapper = document.createElement('div')
  wrapper.className = 'item-popup'

  const title = document.createElement('strong')
  title.textContent = item.title

  const meta = document.createElement('div')
  meta.className = 'item-popup-meta'
  meta.textContent = [t('travelLog'), item.visitedAt || t('noDate')].join(
    ' · ',
  )

  
  void renderMedia(wrapper, item, 'popup-media')
  wrapper.append(title, meta)
  
  if (item.note) {
    const note = document.createElement('div')
    note.textContent = item.note
    wrapper.append(note)
  }

  if (controls) {
    const actions = document.createElement('div')
    actions.className = 'item-popup-actions'
    const editButton = document.createElement('button')
    editButton.type = 'button'
    editButton.className = 'button secondary item-popup-edit'
    editButton.textContent = t('edit')
    editButton.addEventListener('click', () => {
      map.closePopup()
      controls.onEdit(item)
    })
    const deleteButton = document.createElement('button')
    deleteButton.type = 'button'
    deleteButton.className = 'button secondary item-popup-delete'
    deleteButton.textContent = t('delete')
    deleteButton.addEventListener('click', () => {
      controls.onDelete(item)
    })
    actions.append(editButton, deleteButton)
    wrapper.append(actions)
  }

  return wrapper
}

// ---- Camera helpers ---------------------------------------------------------

export function centerCountry(countryName: string): void {
  const feature = findCountryFeature(countryName)
  if (!feature) return
  const bounds = L.geoJSON(feature as GeoJSON.Feature).getBounds()
  if (!bounds.isValid()) return
  map.panTo(bounds.getCenter(), { animate: true })
}

export function centerMapOnLatLng(
  latlng: L.LatLng,
  zoom: number,
  callback: () => void,
): void {
  const currentCenter = map.getCenter()
  const alreadyCentered =
    currentCenter.distanceTo(latlng) < 1 && map.getZoom() === zoom
  if (alreadyCentered) {
    callback()
    return
  }
  map.once('moveend', callback)
  map.setView(latlng, zoom, { animate: true })
}
